import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { label: '首页', to: '/' },
  { label: '电影', to: '/category/movie' },
  { label: '电视剧', to: '/category/tv' },
  { label: '综艺', to: '/category/variety' },
  { label: '关于', to: '/about' },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
  };

  const isActive = (to: string) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled ? 'bg-background-50/95 border-b border-background-200/70' : 'bg-background-50/80'
      } backdrop-blur`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 md:h-20 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0 cursor-pointer">
            <span className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg bg-primary-700 text-accent-400">
              <i className="ri-clapperboard-line text-xl md:text-2xl"></i>
            </span>
            <span className="flex flex-col leading-tight">
              <span className="font-heading font-black text-base md:text-lg text-primary-800 whitespace-nowrap">
                亚洲中文字幕
              </span>
              <span className="font-brand text-[10px] md:text-xs tracking-[0.22em] text-accent-600 uppercase">
                Asia Subtitle TV
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  isActive(item.to)
                    ? 'text-accent-600 bg-accent-50'
                    : 'text-foreground-700 hover:text-primary-700 hover:bg-background-100'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form
              onSubmit={submitSearch}
              className="hidden lg:flex items-center h-10 rounded-full bg-background-100 border border-background-200 focus-within:border-primary-400 pl-3 pr-1 transition-colors"
            >
              <i className="ri-search-line text-foreground-400 text-base"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影片、剧集、综艺"
                className="w-44 bg-transparent px-2 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none"
              />
              <button
                type="submit"
                className="h-8 px-3.5 rounded-full bg-accent-500 hover:bg-accent-600 text-white text-xs font-medium whitespace-nowrap transition-colors cursor-pointer"
              >
                搜索
              </button>
            </form>

            <Link
              to="/search"
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
              aria-label="搜索"
            >
              <i className="ri-search-line text-xl"></i>
            </Link>

            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-800 hover:bg-background-100 cursor-pointer"
              aria-label="菜单"
            >
              <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200/70 bg-background-50 px-4 pb-4 pt-3">
          <form onSubmit={submitSearch} className="flex items-center h-11 rounded-full bg-background-100 border border-background-200 pl-3 pr-1 mb-3">
            <i className="ri-search-line text-foreground-400 text-base"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影片、剧集、综艺"
              className="flex-1 bg-transparent px-2 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none"
            />
            <button
              type="submit"
              className="h-9 px-4 rounded-full bg-accent-500 hover:bg-accent-600 text-white text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
            >
              搜索
            </button>
          </form>
          <nav className="grid grid-cols-2 gap-2">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`px-4 py-3 rounded-lg text-sm font-medium cursor-pointer ${
                  isActive(item.to)
                    ? 'bg-primary-700 text-white'
                    : 'bg-background-100 text-foreground-800'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}